import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiX } from 'react-icons/fi'

const AdminModal = ({ isOpen, onClose, onSave, data, columns, title }) => {
  const [formData, setFormData] = useState({})

  useEffect(() => {
    if (data) {
      setFormData(data)
    } else {
      const initialData = {}
      columns.forEach(col => {
        if (col.accessor !== 'id' && col.accessor !== 'created_at' && col.accessor !== 'updated_at') {
          initialData[col.accessor] = ''
        }
      })
      setFormData(initialData)
    }
  }, [data, columns, isOpen])

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave(formData)
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div 
          onClick={handleOverlayClick}
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="bg-dark-surface border border-white/10 rounded-lg sm:rounded-xl shadow-2xl w-full max-w-sm sm:max-w-lg overflow-hidden flex flex-col"
          >
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-white/10">
              <h2 className="text-lg sm:text-2xl font-bold font-grotesk text-white">
                {data ? `Edit ${title}` : `Add New ${title}`}
              </h2>
              <button onClick={onClose} className="text-gray-400 hover:text-white transition">
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3 sm:space-y-4 max-h-[65vh] md:max-h-[70vh] overflow-y-auto">
              {columns.map((col, index) => {
                if (col.accessor === 'id' || col.accessor === 'created_at' || col.accessor === 'updated_at') return null

                return (
                  <div key={index}>
                    <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
                      {col.header}
                    </label>
                    {col.type === 'select' ? (
                       <select
                         name={col.accessor}
                         value={formData[col.accessor] || ''}
                         onChange={handleChange}
                         className="w-full px-3 py-2 text-sm bg-dark-bg border border-white/20 rounded-lg text-white focus:outline-none focus:border-accent-cyan"
                         required={!col.optional}
                       >
                         <option value="">Select...</option>
                         {col.options?.map(opt => (
                           <option key={opt} value={opt}>{opt}</option>
                         ))}
                       </select>
                     ) : col.type === 'textarea' ? (
                       <textarea
                         name={col.accessor}
                         value={formData[col.accessor] || ''}
                         onChange={handleChange}
                         rows="3"
                         className="w-full px-3 py-2 text-sm bg-dark-bg border border-white/20 rounded-lg text-white focus:outline-none focus:border-accent-cyan resize-none"
                         required={!col.optional}
                       />
                     ) : col.type === 'time' ? (
                       <input
                         type="time"
                         name={col.accessor}
                         value={formData[col.accessor] || ''}
                         onChange={handleChange}
                         className="w-full px-3 py-2 text-sm bg-dark-bg border border-white/20 rounded-lg text-white focus:outline-none focus:border-accent-cyan"
                         required={!col.optional}
                       />
                     ) : (
                       <input
                         type={col.type || 'text'}
                         name={col.accessor}
                         value={formData[col.accessor] || ''}
                         onChange={handleChange}
                         className="w-full px-3 py-2 text-sm bg-dark-bg border border-white/20 rounded-lg text-white focus:outline-none focus:border-accent-cyan"
                         required={!col.optional}
                       />
                     )}
                  </div>
                )
              })}

              <div className="pt-3 sm:pt-4 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg text-gray-300 hover:bg-white/5 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg bg-accent-cyan text-dark-bg font-semibold hover:bg-accent-cyan/90 transition"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default AdminModal
